import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Zap, Crown, ArrowRight, BarChart3, Brain, X, SparkleIcon } from 'lucide-react';
import { PLAN_NAME_MAPPER } from '@/constants';
import { usePlans } from '@/contexts/PlanRulesContext';
import { formatCurrency } from '@/helpers/formatters';

// Pay As You Go Plans


const planDesignMapper = {
    [PLAN_NAME_MAPPER.basic]: {
        icon: Zap,
        popular: false,
        buttonText: 'Choose Starter',
        buttonVariant: 'default',
    },
    [PLAN_NAME_MAPPER.advance]: {
        icon: Crown,
        popular: false,
        buttonText: 'Choose Professional',
        buttonVariant: 'premium',
    },
    [PLAN_NAME_MAPPER.regular]: {
        icon: BarChart3,
        buttonText: 'Get Started',
        buttonVariant: 'outline',
        color: 'blue',
    },
    [PLAN_NAME_MAPPER.regularWithAI]: {
        icon: Brain,
        buttonText: 'Get Started',
        buttonVariant: 'default',
        color: 'purple',
        recommended: true,
    }
}

export default function PricingSection() {
    const navigate = useNavigate();

    const { plans, isLoadingPlans } = usePlans();

    if (isLoadingPlans || !plans) return null;

    const paygPlans = plans?.plans
        ?.filter(plan => plan?.type == "regular")
        ?.map(plan => ({ ...plan, ...planDesignMapper[plan?.name] }))
    const subscriptionPlans = plans?.plans
        ?.filter(plan => plan?.type == "subscription")
        ?.map(plan => ({ ...plan, ...planDesignMapper[plan?.name] }))

    const handlePlanSelect = (planId) => {
        // Navigate to register with selected plan
        navigate(`/register?plan=${planId}`);
    };

    return (
        <section id="pricing" className="py-20 lg:py-28 bg-gradient-to-b from-background to-muted/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <Badge variant="outline" className="mb-4 px-4 py-1.5">
                        Simple Pricing
                    </Badge>
                    <p className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                        Choose Your Plan
                    </p>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Start with Pay As You Go or subscribe monthly for more features. No hidden fees.
                    </p>
                </div>

                {/* Pay As You Go Section */}
                <div className="mb-12">
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 px-4 py-1">
                            Lite Plans
                        </Badge>
                        <span className="text-sm text-muted-foreground">Perfect for trying out</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                        {paygPlans.map((plan) => {

                            const Icon = plan?.icon || SparkleIcon

                            return (
                                <Card
                                    key={plan.id}
                                    className={`relative flex flex-col ${plan?.recommended
                                        ? 'border-purple-500 shadow-xl shadow-purple-500/10'
                                        : 'border-border hover:border-blue-500/50 transition-colors'
                                        }`}
                                >
                                    {plan?.recommended && (
                                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                            <Badge className="bg-purple-500 text-white px-4 py-1">
                                                {plan?.recommended_text || 'Recommended'}
                                            </Badge>
                                        </div>
                                    )}

                                    <CardHeader className="text-center pb-4">
                                        <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${plan?.color === 'purple' ? 'bg-purple-500/10' : 'bg-blue-500/10'
                                            }`}>
                                            <Icon className={`w-6 h-6 ${plan?.color === 'purple' ? 'text-purple-500' : 'text-blue-500'
                                                }`} />
                                        </div>
                                        <CardTitle className="text-xl">{plan?.name}</CardTitle>
                                        <CardDescription>{plan?.description}</CardDescription>
                                    </CardHeader>

                                    <CardContent className="flex-1 flex flex-col">
                                        {/* Price */}
                                        <div className="text-center mb-6">
                                            <div className="flex items-baseline justify-center gap-1">
                                                <span className="text-4xl font-bold">{formatCurrency(plan?.amount, plan?.currency)}</span>
                                                <span className="text-muted-foreground">/{plan?.period}</span>
                                            </div>
                                        </div>

                                        {/* Features */}
                                        <ul className="space-y-2 mb-6 flex-1">
                                            {plan?.features?.map((feature, idx) => (
                                                <li key={idx} className="flex items-start gap-3 text-sm">
                                                    <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan?.color === 'purple' ? 'text-purple-500' : 'text-blue-500'
                                                        }`} />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                            {plan?.excluded_features?.map((feature, idx) => (
                                                <li key={`ex-${idx}`} className="flex items-start gap-3 text-sm">
                                                    <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-400" />
                                                    <span className="text-muted-foreground/60 line-through">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {/* CTA Button */}
                                        <Button
                                            variant={plan?.buttonVariant}
                                            size="lg"
                                            className={`w-full group ${plan?.color === 'purple' ? 'bg-purple-500 hover:bg-purple-600 text-white' : ''
                                                }`}
                                            onClick={() => handlePlanSelect(plan?.id)}
                                        >
                                            {plan?.buttonText} - {formatCurrency(plan?.amount, plan?.currency)}
                                            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                                        </Button>
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-4 max-w-3xl mx-auto mb-12">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-sm text-muted-foreground font-medium px-4">OR SUBSCRIBE MONTHLY</span>
                    <div className="flex-1 h-px bg-border" />
                </div>

                {/* Monthly Subscriptions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {subscriptionPlans.map((plan) => {

                        const Icon = plan?.icon || SparkleIcon

                        return (
                            <Card
                                key={plan?.id}
                                className={`relative flex flex-col ${plan?.popular
                                    ? 'border-primary shadow-xl shadow-primary/10 scale-105 z-10'
                                    : 'border-border hover:border-primary/50 transition-colors'
                                    }`}
                            >
                                {plan?.recommended && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <Badge className="bg-primary text-primary-foreground px-4 py-1">
                                            {plan?.recommended_text || 'Recommended'}
                                        </Badge>
                                    </div>
                                )}

                                {/* offer badge */}
                                {plan?.offers?.is_offer && (
                                    <div className="absolute top-3 right-3">
                                        <Badge className="bg-orange-500 text-white px-2 py-1 text-xs font-semibold">
                                            {plan.offers.offer_percent}% OFF
                                        </Badge>
                                    </div>
                                )}


                                <CardHeader className="text-center pb-4">
                                    <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${plan?.popular ? 'bg-primary text-primary-foreground' : 'bg-amber-500/10'
                                        }`}>
                                        <Icon className={`w-6 h-6 ${!plan?.popular ? 'text-amber-500' : ''}`} />
                                    </div>
                                    <CardTitle className="text-xl">{plan?.name}</CardTitle>
                                    <CardDescription>{plan?.description}</CardDescription>
                                </CardHeader>

                                <CardContent className="flex-1 flex flex-col">
                                    {/* Price */}
                                    <div className="text-center mb-6">
                                        <div className="flex flex-col items-center gap-1">

                                            {/* Discounted Price */}
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-4xl font-bold">
                                                    {formatCurrency(
                                                        plan?.offers?.is_offer ? plan?.offers?.offer_price : plan?.amount,
                                                        plan?.currency
                                                    )}
                                                </span>

                                                <span className="text-muted-foreground text-sm">
                                                    /{plan?.period}
                                                </span>
                                            </div>

                                            {/* Original Price (only if offer exists) */}
                                            {plan?.offers?.is_offer && (
                                                <span className="text-lg text-muted-foreground line-through">
                                                    {formatCurrency(plan?.amount, plan?.currency)}
                                                </span>
                                            )}

                                            {/* {plan?.offers?.is_offer && (
                                                <span className="text-xs text-green-600 font-medium">
                                                    Save {formatCurrency(plan.amount - plan.offers.offer_price, plan.currency)}
                                                </span>
                                            )} */}

                                        </div>
                                    </div>



                                    {/* Features */}
                                    <ul className="space-y-3 mb-8 flex-1">
                                        {plan?.features?.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-3 text-sm">
                                                <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan?.popular ? 'text-primary' : 'text-amber-500'
                                                    }`} />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                        {plan?.excluded_features?.map((feature, idx) => (
                                            <li key={`ex-${idx}`} className="flex items-start gap-3 text-sm">
                                                <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-400" />
                                                <span className="text-muted-foreground/60 line-through">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA Button */}
                                    <Button
                                        variant={plan?.buttonVariant}
                                        size="lg"
                                        className={`w-full group ${!plan.popular ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                                        onClick={() => handlePlanSelect(plan?.id)}
                                    >
                                        {plan?.buttonText}
                                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                                    </Button>
                                </CardContent>
                            </Card>
                        )
                    })}
                </div>

                {/* Trust Badges */}
                <div className="mt-16 text-center">
                    <p className="text-sm text-muted-foreground mb-4">
                        All plans include:
                    </p>
                    <div className="flex flex-wrap justify-center gap-4 text-sm">
                        <span className="px-4 py-2 bg-muted rounded-full">No setup fees</span>
                        <span className="px-4 py-2 bg-muted rounded-full">Cancel anytime</span>
                        <span className="px-4 py-2 bg-muted rounded-full">Secure payments via Razorpay</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
